import uniqid from 'uniqid';
import { getNotGroupOperator } from 'src/utils';
import { isEmpty } from '../utils/misc';

const sortArguments = (list) => {
  const types = ['block'];
  return list
    .filter((item) => item)
    .sort((a, b) => types.indexOf(a.type) - types.indexOf(b.type));
};

export const findGroupAndAddEntity = (query, parent_group_id, updateItem) => {
  if (parent_group_id === 0) {
    const exist =
      typeof query.arguments_list.find(
        (item) => updateItem && item.id === updateItem.id
      ) === 'object';
    if (!exist) {
      return {
        ...query,
        arguments_list: sortArguments([...query.arguments_list, updateItem]),
      };
    }

    return {
      ...query,
      arguments_list: sortArguments(
        query.arguments_list.map((item) => {
          if (item.id !== updateItem.id) {
            return item;
          }
          return {
            ...item,
            ...updateItem,
          };
        })
      ),
    };
  }

  return {
    ...query,
    arguments_list: sortArguments(
      query.arguments_list.map((item) => {
        if (item.id !== parent_group_id) {
          if (!item.arguments_list || item.arguments_list.length === 0) {
            return item;
          }

          return {
            ...item,
            ...findGroupAndAddEntity(item, parent_group_id, updateItem),
          };
        }

        const exist =
          typeof item.arguments_list.find(
            (i) => updateItem && i.id === updateItem.id
          ) === 'object';
        if (!exist) {
          return {
            ...item,
            arguments_list: sortArguments([...item.arguments_list, updateItem]),
          };
        }
        return item;
      })
    ),
  };
};

/** Updating via index since in the query builder is allowed to have duplicated items even in the same group */
export const findEntityAndUpdate = (
  query,
  parent_group_id,
  updatedInfos,
  entityIndex
) => {
  if (parent_group_id === 0) {
    return {
      ...query,
      arguments_list: query.arguments_list.map((item, index) => {
        if (index !== entityIndex) {
          return item;
        }
        return {
          ...item,
          type: updatedInfos.type,
          id: updatedInfos.id,
          source: updatedInfos.source,
          class: updatedInfos.class,
          metadata: updatedInfos.metadata,
        };
      }),
    };
  }

  return {
    ...query,
    arguments_list: query.arguments_list.map((item) => {
      if (item.id !== parent_group_id) {
        if (!item.arguments_list || item.arguments_list.length === 0) {
          return item;
        }

        return {
          ...item,
          ...findEntityAndUpdate(
            item,
            parent_group_id,
            updatedInfos,
            entityIndex
          ),
        };
      }

      return {
        ...item,
        arguments_list: item.arguments_list.map((argument, index) => {
          if (index !== entityIndex) {
            return argument;
          }
          return {
            ...argument,
            type: updatedInfos.type,
            id: updatedInfos.id,
            source: updatedInfos.source,
            class: updatedInfos.class,
            metadata: updatedInfos.metadata,
          };
        }),
      };
    }),
  };
};

export const createBlankSearchQuery = (queryBuilderType = 'default') => {
  const groupId = uniqid();
  const entityId = uniqid();
  const item =
    queryBuilderType === 'conversation'
      ? { type: 'conversation', id: entityId }
      : { type: 'item', id: `tmp_${entityId}` };

  return {
    type: 'block',
    id: 0,
    operator: 'OR',
    arguments_list: [
      {
        type: 'block',
        id: groupId,
        operator: 'OR',
        arguments_list: [item],
        selected: true,
      },
    ],
  };
};

export const isPlaceholderQueryEntity = (item) => {
  if (!item) return false;
  if (typeof item.id === 'string' && item.id.includes('tmp_')) return true;
  return item.type === 'conversation' && !item.value;
};

export const isGroupOnlyEmptyRow = (
  group,
  entitiesInfos = {},
  conversationQueries = {}
) => {
  const items = group?.arguments_list || [];
  if (items.length !== 1) return false;
  const [item] = items;
  if (!item || item.type === 'block') return false;
  if (isPlaceholderQueryEntity(item)) {
    return item.type !== 'conversation'
      ? true
      : !conversationQueries[`${group.id}_${item.id}`];
  }
  return (
    item.type !== 'conversation' && !entitiesInfos[`${item.type}_${item.id}`]
  );
};

export const findEntity = (query, parent_group_id, entityIndex) => {
  if (parent_group_id === 0 || parent_group_id === '0') {
    return query.arguments_list.find((_, index) => index === entityIndex);
  }
  let result = null;
  query.arguments_list.forEach((item) => {
    if (item.id !== parent_group_id) {
      if (!isEmpty(item.arguments_list) && !result) {
        result = findEntity(item, parent_group_id, entityIndex);
      }
    } else {
      if (!isEmpty(item.arguments_list) && !result) {
        result = item.arguments_list.find((_, index) => index === entityIndex);
      }
    }
  });
  return result;
};

export const findSelectedGroup = (node) => {
  if (node.selected) {
    return node.id;
  }
  let result = null;
  if (!isEmpty(node.arguments_list)) {
    node.arguments_list.forEach((n) => {
      const r = findSelectedGroup(n);

      if (r) {
        result = r;
      }
    });
  }
  return result;
};

/** Removing by index since in the query builder is allowed to have duplicated items even in the same group */
export const findAndRemoveEntity = (query, parent_group_id, entityIndex) => {
  if (parent_group_id === 0 || parent_group_id === '0') {
    return {
      ...query,
      arguments_list: query.arguments_list.filter(
        (_, index) => index !== entityIndex
      ),
    };
  }

  return {
    ...query,
    arguments_list: query.arguments_list.map((item) => {
      if (item.id !== parent_group_id) {
        if (!item.arguments_list || item.arguments_list.length === 0) {
          return item;
        }

        return {
          ...item,
          ...findAndRemoveEntity(item, parent_group_id, entityIndex),
        };
      }

      return {
        ...item,
        arguments_list: item.arguments_list.filter(
          (_, index) => index !== entityIndex
        ),
      };
    }),
  };
};

export const findAndChangeGroupOperator = (query, group_id, new_operator) => {
  if (group_id === 0) {
    return {
      ...query,
      operator: new_operator,
    };
  }

  return {
    ...query,
    arguments_list: query.arguments_list.map((item) => {
      if (item.id !== group_id) {
        if (!item.arguments_list || item.arguments_list.length === 0) {
          return item;
        }

        return {
          ...item,
          ...findAndChangeGroupOperator(item, group_id, new_operator),
        };
      }

      return {
        ...item,
        operator: new_operator,
      };
    }),
  };
};

export const findAndChangeGroupAtLeastNumber = (query, group_id, at_least) => {
  if (group_id === 0) {
    return {
      ...query,
      at_least,
    };
  }

  return {
    ...query,
    arguments_list: query.arguments_list.map((item) => {
      if (item.id !== group_id) {
        if (!item.arguments_list || item.arguments_list.length === 0) {
          return item;
        }

        return {
          ...item,
          ...findAndChangeGroupAtLeastNumber(item, group_id, at_least),
        };
      }

      return {
        ...item,
        at_least,
      };
    }),
  };
};

export const findAndSelectGroup = (query, group_id, isItemNoGroup = false) => {
  if (group_id === 0) {
    return {
      ...query,
      selected: query.id === group_id,
    };
  }

  return {
    ...query,
    selected: query.id === group_id,
    arguments_list: query.arguments_list.map((item) => {
      if (isItemNoGroup) {
        return {
          type: 'block',
          id: group_id,
          operator: 'OR',
          arguments_list: [{ ...item }],
          selected: true,
        };
      }

      if (!item.arguments_list || item.arguments_list.length === 0) {
        return { ...item, selected: item.id === group_id };
      }

      return {
        ...item,
        selected: item.id === group_id,
        ...findAndSelectGroup(item, group_id),
      };
    }),
  };
};

export const findItemNested = (query, itemId) => {
  return query.arguments_list.reduce((a, item) => {
    if (a) {
      return a;
    }
    if (item.id === itemId) {
      return item;
    }
    if (item.arguments_list) {
      return findItemNested(item, itemId);
    }

    return null;
  }, null);
};

export const findAndMoveEntity = (
  query,
  parentIdFrom,
  entityIndex,
  itemId,
  parentIdTo
) => {
  const itemMoving = findItemNested(query, itemId);
  const dataWithRemoved = findAndRemoveEntity(query, parentIdFrom, entityIndex);
  const dataWithMoved = findGroupAndAddEntity(
    dataWithRemoved,
    parentIdTo,
    itemMoving
  );

  return dataWithMoved;
};

/**
 * Child nodes for walking the query tree (metadata, membership, etc.).
 * NOT blocks may use `argument` while `arguments_list` is empty; `??` alone misses that.
 */
const getQueryNodeChildren = (node) => {
  if (!node || typeof node !== 'object') {
    return [];
  }
  if (!isEmpty(node.arguments_list)) {
    return node.arguments_list;
  }
  const arg = node.argument;
  if (arg && typeof arg === 'object') {
    if (!isEmpty(arg.arguments_list)) {
      return arg.arguments_list;
    }
    return [arg];
  }
  return [];
};

export const getQueryItemsIds = (query, itemType) => {
  return getQueryNodeChildren(query).reduce((acc, item) => {
    if (item.type === 'block') {
      return [...acc, ...getQueryItemsIds(item, itemType)];
    }

    if (itemType && itemType !== item.type) {
      return acc;
    }

    if (item.id == null || item.id === '') {
      return acc;
    }

    return [...acc, item.id];
  }, []);
};

/** Stanley/API NOT block (`operator: 'NOT'`) vs builder exclude group (`NOT (AND|OR)`). */
const isApiNotBlock = (node) =>
  node?.type === 'block' && node.operator === 'NOT';

const isUiNotGroup = (operator) =>
  operator === 'NOT (AND)' || operator === 'NOT (OR)';

const notUiOperator = (parentOperator) =>
  `NOT (${parentOperator === 'OR' ? 'OR' : 'AND'})`;

/** Excluded operands inside an API NOT block (`argument` or `arguments_list`). */
const getNotExclusionOperands = (notBlock) => {
  const inner = notBlock.argument;
  if (inner?.type === 'block') {
    const items = inner.arguments_list || [];
    const keepGrouped = items.length > 1 || items[0]?.type === 'block';
    return keepGrouped ? [inner] : items;
  }
  return notBlock.arguments_list || [];
};

const splitAroundApiNot = (argumentsList) => {
  const siblings = [];
  let notBlock;
  for (const arg of argumentsList || []) {
    if (!notBlock && isApiNotBlock(arg)) {
      notBlock = arg;
    } else {
      siblings.push(arg);
    }
  }
  return { notBlock, siblings };
};

/** Legacy flat list for root-level NOT (AND) promotion (tests / affinity parity). */
export const aggregateNotAttributes = (tree, notBlock) => {
  const { siblings } = splitAroundApiNot(tree.arguments_list);
  return [...siblings, ...getNotExclusionOperands(notBlock)];
};

/**
 * API shape: AND/OR parent + NOT sibling → keep parent operator, add nested NOT (AND|OR) child.
 * Already-UI NOT groups are passed through unchanged.
 */
const applyStanleyNotLayout = (tree, isFirst, buildBlock) => {
  if (isUiNotGroup(tree.operator)) {
    return buildBlock(tree, isFirst);
  }

  const { notBlock, siblings } = splitAroundApiNot(tree.arguments_list);
  const exclusions = notBlock ? getNotExclusionOperands(notBlock) : [];
  if (!exclusions.length) {
    return buildBlock(tree, isFirst);
  }

  const excludeGroup = {
    type: 'block',
    operator: notUiOperator(tree.operator),
    arguments_list: exclusions,
  };

  if (!siblings.length) {
    return buildBlock({ ...excludeGroup, at_least: tree.at_least }, isFirst);
  }

  return buildBlock(
    { ...tree, arguments_list: [...siblings, excludeGroup] },
    isFirst
  );
};

/** Assign ids to NOT (AND|OR) groups and their children (Stanley apply leaves them id-less). */
const formatNotGroup = (notGroup) => {
  const block = {
    id: uniqid(),
    type: 'block',
    operator: notGroup.operator,
    arguments_list: (notGroup.arguments_list || []).map((arg) =>
      mapQueryBlocksWithUniqueIdAndFormat(arg, false)
    ),
  };
  return notGroup.at_least != null
    ? { ...block, at_least: notGroup.at_least }
    : block;
};

const buildFormattedBlock = (tree, isFirst) => {
  const block = {
    id: isFirst ? 0 : uniqid(),
    type: 'block',
    operator: tree.operator,
    arguments_list: (tree.arguments_list || []).map((arg) =>
      isUiNotGroup(arg.operator)
        ? formatNotGroup(arg)
        : mapQueryBlocksWithUniqueIdAndFormat(arg, false)
    ),
  };
  return tree.at_least != null ? { ...block, at_least: tree.at_least } : block;
};

export const mapQueryBlocksWithUniqueIdAndFormat = (tree, isFirst = true) => {
  if (tree.type !== 'block') {
    return tree;
  }
  return applyStanleyNotLayout(tree, isFirst, buildFormattedBlock);
};

/**
 * Remaps a subtree for use as one operand under a new root AND.
 * Strips redundant single-child OR wrappers left over from each target's
 * former root (id 0): that OR was not shown as a separate group in the UI;
 * nesting it under AND gives it a new id, which surfaced an "extra" OR row.
 */
const mapQueryOperandForAndMerge = (tree) => {
  let mapped = mapQueryBlocksWithUniqueIdAndFormat(tree, false);
  while (
    mapped &&
    mapped.type === 'block' &&
    mapped.operator === 'OR' &&
    mapped.arguments_list?.length === 1 &&
    mapped.arguments_list[0].type === 'block'
  ) {
    mapped = mapped.arguments_list[0];
  }
  return mapped;
};

/**
 * Merges two query trees for "copy into existing target": destination first,
 * then source, combined at the root with AND. Remaps both subtrees with fresh
 * block ids so the merged query is valid in the builder.
 */
export const appendQueryWithAndOperator = (destTree, sourceTree) => {
  const destEmpty = isEmpty(destTree?.arguments_list);
  const sourceEmpty = isEmpty(sourceTree?.arguments_list);
  if (destEmpty && sourceEmpty) {
    return {
      type: 'block',
      id: 0,
      operator: 'OR',
      arguments_list: [],
    };
  }
  if (destEmpty) {
    return mapQueryBlocksWithUniqueIdAndFormat(sourceTree);
  }
  if (sourceEmpty) {
    return mapQueryBlocksWithUniqueIdAndFormat(destTree);
  }
  return {
    type: 'block',
    id: 0,
    operator: 'AND',
    arguments_list: [
      mapQueryOperandForAndMerge(destTree),
      mapQueryOperandForAndMerge(sourceTree),
    ],
  };
};

export const mapArgument = (argument) => {
  if (argument.type) {
    if (argument.type.includes('demographic')) {
      return {
        id: argument.id,
        type: 'demographic',
        class: argument.class ? argument.class : argument.type.split('.')[1],
        source: argument.source,
      };
    }

    if (argument.type === 'membership') {
      return {
        id: argument.id,
        type: argument.type,
        class: argument.class,
        source: argument.source,
      };
    }

    if (argument.type === 'conversation') {
      if (typeof argument.value !== 'undefined') {
        return {
          value: argument.value,
          type: argument.type,
          source: argument.source,
        };
      }
      return undefined;
    }
  }

  if (argument.id.includes('all_')) {
    return {
      id: argument.parent_id,
      type: argument.type,
      source: argument.source,
    };
  }

  return {
    id: argument.id,
    type: argument.type || 'category',
    source: argument.source,
  };
};

const clearApiNotBlock = (block) => {
  if (isUiNotGroup(block.operator)) {
    return clearUiNotGroup(block);
  }
  if (block.arguments_list?.length > 0) {
    return clearQuery(block);
  }
  if (block.argument) {
    const cleared = clearQuery(block.argument);
    return cleared ? { ...block, argument: cleared } : null;
  }
  return null;
};

/** Map one operand while converting a UI NOT (AND|OR) group for API serialization. */
const mapNotGroupOperand = (argument) => {
  if (argument.type !== 'block') {
    if (['interests', 'conversation'].includes(argument.type)) {
      if (argument.metadata) {
        return argument.metadata.target_query;
      }
      const mappedArgument = mapArgument(argument);
      return mappedArgument || argument;
    }
    if (!argument.id.includes('tmp_')) {
      const mappedArgument = mapArgument(argument);
      return mappedArgument || argument;
    }
    return null;
  }
  return argument;
};

/**
 * UI NOT (AND|OR) → API `NOT` block.
 * Legacy root layout: first child block is positive, remaining operands are exclusions.
 * Stanley nested layout: all children are exclusions → wrap in `NOT { argument: ... }`.
 */
const clearUiNotGroup = (query) => {
  const groupOperator = getNotGroupOperator(query.operator);
  const argsListCopy = (query.arguments_list || [])
    .map(mapNotGroupOperand)
    .filter(Boolean);
  const firstArgument = argsListCopy.shift();
  const isLegacyNotSplit =
    firstArgument?.type === 'block' && argsListCopy.length > 0;

  if (isLegacyNotSplit) {
    const newArgumentList = [
      firstArgument.arguments_list ? clearQuery(firstArgument) : firstArgument,
    ];
    newArgumentList.push({
      type: 'block',
      operator: 'NOT',
      argument: clearQuery({
        ...query,
        arguments_list: argsListCopy,
        operator: groupOperator,
      }),
    });
    return {
      type: 'block',
      operator: groupOperator,
      arguments_list: newArgumentList,
    };
  }

  const exclusionOperands = firstArgument
    ? [firstArgument, ...argsListCopy]
    : argsListCopy;

  return {
    type: 'block',
    operator: 'NOT',
    argument: clearQuery({
      type: 'block',
      operator: groupOperator,
      arguments_list: exclusionOperands,
    }),
  };
};

export const clearQuery = (query) => {
  //* * This function is used to clear a query for API call:
  // If the operator is has NOT it wraps the block in a NOT block
  // removes any block with empty agruments_list
  // removes prop id from block
  // removes items with temporary ids */
  if (isUiNotGroup(query.operator)) {
    return clearUiNotGroup(query);
  }

  const block = {
    type: 'block',
    operator: query.operator,
    arguments_list: query.arguments_list.reduce((acc, argument) => {
      if (argument.type !== 'block') {
        if (['interests', 'conversation'].includes(argument.type)) {
          if (argument.metadata) {
            return [...acc, clearQuery(argument.metadata.target_query)];
          } else {
            const mappedArgument = mapArgument(argument);
            return mappedArgument ? [...acc, mappedArgument] : acc;
          }
        }
        if (!argument.id.includes('tmp_')) {
          const mappedArgument = mapArgument(argument);
          return mappedArgument ? [...acc, mappedArgument] : acc;
        }
      }
      if (argument.type === 'block') {
        const cleared = clearApiNotBlock(argument);
        return cleared ? [...acc, cleared] : acc;
      }

      return acc;
    }, []),
  };
  if (query.operator === 'AT_LEAST') {
    return {
      ...block,
      at_least: (query.at_least && Number(query.at_least)) || 0,
    };
  }
  return block;
};

/**
 * Same preprocessing as Stanley membership size ({@link buildStanleyMembershipBody}):
 * strip empty block shells, then {@link clearQuery} so builder apply matches API semantics.
 */
export const preprocessStanleyTargetQueryForApply = (rawTargetQuery) => {
  if (rawTargetQuery == null || typeof rawTargetQuery !== 'object') {
    return null;
  }
  try {
    const clonedQuery = JSON.parse(JSON.stringify(rawTargetQuery));
    const stripped = removeEmptyBlocksFromQuery(clonedQuery, false);
    if (!stripped || typeof stripped !== 'object') {
      return null;
    }
    return clearQuery(stripped);
  } catch {
    return null;
  }
};

export const getQueryEntityInfoKey = (item) => {
  if (item.type.includes('demographic')) {
    return `demographic_${item.id}`;
  }

  return `${item.type}_${item.id}`;
};

export const getQueryItemSourcesByKey = (query) => {
  const sources = {};

  getQueryNodeChildren(query).forEach((item) => {
    if (item.type === 'block') {
      Object.assign(sources, getQueryItemSourcesByKey(item));
      return;
    }

    if (item.source) {
      sources[getQueryEntityInfoKey(item)] = item.source;
    }
  });

  return sources;
};

export const mergeMetadataWithQuerySources = (metadataItems, ...queries) => {
  if (!metadataItems?.length) {
    return metadataItems ?? [];
  }

  const sourceByKey = queries.reduce((acc, query) => {
    if (!query) {
      return acc;
    }

    return { ...acc, ...getQueryItemSourcesByKey(query) };
  }, {});

  if (Object.keys(sourceByKey).length === 0) {
    return metadataItems;
  }

  return metadataItems.map((item) => {
    const querySource = sourceByKey[getQueryEntityInfoKey(item)];

    return querySource ? { ...item, source: querySource } : item;
  });
};

export const createUniqueInfoIds = (queryEntitiesInfos) => {
  const arrToObj = (arr) => {
    const obj = {};

    arr.forEach((item) => {
      obj[getQueryEntityInfoKey(item)] = item;
    });

    return obj;
  };

  const withUniqueInfoIds = arrToObj(queryEntitiesInfos);

  return withUniqueInfoIds;
};

export const findConversationEntityAndUpdate = (
  query,
  parent_group_id,
  entityContent,
  entityIndex
) => {
  if (parent_group_id === 0) {
    return {
      ...query,
      arguments_list: query.arguments_list.map((item, index) => {
        if (index !== entityIndex) {
          return item;
        }
        return {
          ...item,
          value: entityContent,
        };
      }),
    };
  }

  return {
    ...query,
    arguments_list: query.arguments_list.map((item) => {
      if (item.id !== parent_group_id) {
        if (!item.arguments_list || item.arguments_list.length === 0) {
          return item;
        }

        return {
          ...item,
          ...findConversationEntityAndUpdate(
            item,
            parent_group_id,
            entityContent,
            entityIndex
          ),
        };
      }

      return {
        ...item,
        arguments_list: item.arguments_list.map((argument, index) => {
          if (index !== entityIndex) {
            return argument;
          }
          return {
            ...argument,
            value: entityContent,
          };
        }),
      };
    }),
  };
};

const buildConversationBlock = (tree, isFirst, conversationQueries) => {
  const blockId = isFirst ? 0 : uniqid();
  return {
    id: blockId,
    type: 'block',
    operator: tree.operator,
    arguments_list: (tree.arguments_list || []).map((argument) => {
      const id = uniqid();
      if (argument.type !== 'block') {
        conversationQueries[`${blockId}_${id}`] = argument.value;
        return { ...argument, id };
      }
      return isUiNotGroup(argument.operator)
        ? formatNotGroup(argument)
        : mapConversationQueries(argument, false, conversationQueries);
    }),
  };
};

export const mapConversationQueries = (
  tree,
  isFirst = true,
  conversationQueries = {}
) => {
  if (tree.type !== 'block') {
    return tree;
  }
  return applyStanleyNotLayout(tree, isFirst, (subtree, nested) =>
    buildConversationBlock(subtree, nested, conversationQueries)
  );
};

export const checkQuery = (query) => {
  if (!query) {
    return false;
  }
  if (query.type === 'block') {
    const args = query.arguments_list || [];
    const onlyTemporaryLeaves =
      !isEmpty(args) &&
      args.every(
        (item) =>
          item.type !== 'block' &&
          typeof item.id === 'string' &&
          item.id.includes('tmp_')
      );
    const onlyEmptyConversations =
      !isEmpty(args) &&
      args.every((item) => item.type === 'conversation' && !item.value);

    if (isEmpty(args) || onlyTemporaryLeaves || onlyEmptyConversations) {
      return false;
    }
  }

  let result = true;
  if (!isEmpty(query.arguments_list)) {
    query.arguments_list.forEach((item) => {
      result = result && checkQuery(item);
    });
  }
  return result;
};

export const removeEmptyBlocksFromQuery = (node, all = true) => {
  if (node.id === 0 && node.type === 'block' && isEmpty(node.arguments_list)) {
    return node;
  }
  if (node.type === 'block' && isEmpty(node.arguments_list)) {
    if (node.argument) {
      const processedArgument = removeEmptyBlocksFromQuery(node.argument, all);
      return processedArgument
        ? { ...node, argument: processedArgument }
        : null;
    }
    return null;
  }

  const children = [];
  node.arguments_list.forEach((child) => {
    const holder =
      child.type === 'block' ? removeEmptyBlocksFromQuery(child, all) : null;
    if (child.type !== 'block') {
      children.push(child);
    }

    if (holder) {
      children.push({ ...holder });
    }
  });
  if (isEmpty(children)) {
    if (node.id === 0 && node.type === 'block') {
      return all
        ? node
        : {
            id: node.id,
            type: node.type,
            operator: node.operator,
            arguments_list: [],
          };
    }
    return null;
  }
  node.arguments_list = children;
  return node;
};

/** Deep-clone + strip nested empty block shells so "new tab" queries count as empty for AND-merge. */
export const normalizeRootQueryForStanleyMerge = (rootQuery) => {
  const emptyRoot = {
    type: 'block',
    id: 0,
    operator: 'OR',
    arguments_list: [],
  };
  if (!rootQuery || rootQuery.type !== 'block') {
    return emptyRoot;
  }
  const clone = JSON.parse(JSON.stringify(rootQuery));
  const cleaned = removeEmptyBlocksFromQuery(clone, false);
  if (!cleaned || typeof cleaned !== 'object') {
    return emptyRoot;
  }
  return cleaned;
};

export const removeEmptyItemsFromQuery = (node) => {
  const block = {
    ...node,
    arguments_list: node.arguments_list.reduce((acc, argument) => {
      if (argument.type !== 'block') {
        if (!isPlaceholderQueryEntity(argument)) {
          return [...acc, argument];
        }
      } else {
        return [
          ...acc,
          argument.arguments_list?.length > 0
            ? removeEmptyItemsFromQuery(argument)
            : argument,
        ];
      }

      return acc;
    }, []),
  };
  return block;
};

/** Drop placeholder search rows, then place the item in the selected group. */
export const insertQueryItem = (currentQuery, preferredGroupId, newEntity) => {
  const withoutPlaceholders = removeEmptyItemsFromQuery(currentQuery);
  const selectedGroupId = findSelectedGroup(withoutPlaceholders);
  const targetGroupId = preferredGroupId || selectedGroupId;

  const withItem = targetGroupId
    ? findGroupAndAddEntity(withoutPlaceholders, targetGroupId, newEntity)
    : {
        ...withoutPlaceholders,
        arguments_list: [
          ...(withoutPlaceholders.arguments_list || []),
          {
            type: 'block',
            id: uniqid(),
            operator: 'OR',
            arguments_list: [newEntity],
          },
        ],
      };

  return removeEmptyBlocksFromQuery(withItem, false) || withItem;
};
